// Location privacy helpers (PRIVACY_SAFETY §1). Used by the API (authoritative) and the web
// (previews). Exact event coordinates never leave the server except through the serializers;
// everything shown to other users is derived from the snapped cell.

export interface LatLng {
	lat: number;
	lng: number;
}

/** ≈200 m north–south (D42). Cells are ≈200×200 m: the east–west step widens with latitude. */
export const CELL_STEP_DEG = 0.0018;
/** Radius of the circle drawn around a cell center (300 m across, D42); covers the whole cell. */
export const CELL_CIRCLE_RADIUS_M = 150;
/** Snapping is defined for this latitude range (cells get degenerate near the poles). */
export const MAX_ABS_LAT = 85;

const EARTH_RADIUS_M = 6_371_008.8;
const COORD_DECIMALS = 6;

function roundTo(value: number, decimals: number): number {
	const factor = 10 ** decimals;
	return Math.round(value * factor) / factor;
}

function toRad(deg: number): number {
	return (deg * Math.PI) / 180;
}

function assertValid({ lat, lng }: LatLng): void {
	if (!Number.isFinite(lat) || !Number.isFinite(lng)) throw new RangeError('non-finite coordinate');
	if (Math.abs(lat) > MAX_ABS_LAT) throw new RangeError(`|lat| must be ≤ ${MAX_ABS_LAT}`);
	if (Math.abs(lng) > 180) throw new RangeError('|lng| must be ≤ 180');
}

/** East–west step (degrees) for the row whose center latitude is `cellLat`. */
export function lngStepFor(cellLat: number): number {
	return CELL_STEP_DEG / Math.cos(toRad(cellLat));
}

/**
 * Deterministic snapping to the center of the ≈200×200 m cell containing the point.
 * Deterministic on purpose: random jitter can be averaged out by repeated requests.
 * Output is rounded to 6 decimals (≈0.1 m) so server and browsers agree bit-for-bit.
 */
export function snapToCell(point: LatLng): LatLng {
	assertValid(point);
	const cellLat = (Math.floor(point.lat / CELL_STEP_DEG) + 0.5) * CELL_STEP_DEG;
	const lngStep = lngStepFor(cellLat);
	const cellLng = (Math.floor(point.lng / lngStep) + 0.5) * lngStep;
	return { lat: roundTo(cellLat, COORD_DECIMALS), lng: roundTo(cellLng, COORD_DECIMALS) };
}

/** Great-circle distance in meters. */
export function distanceMeters(a: LatLng, b: LatLng): number {
	const dLat = toRad(b.lat - a.lat);
	const dLng = toRad(b.lng - a.lng);
	const h =
		Math.sin(dLat / 2) ** 2 +
		Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
	return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Distance shown to users, in km, rounded to 0.5 km (PRIVACY rule 2). Never below 0.5:
 * "0 km" would wrongly suggest an exact match. Always pass the cell center, never the exact point.
 */
export function displayDistanceKm(meters: number): number {
	if (!Number.isFinite(meters) || meters < 0) throw new RangeError('invalid distance');
	return Math.max(0.5, Math.round((meters / 1000) * 2) / 2);
}

/**
 * The viewer's own position as sent with a request: rounded to 3 decimals (≈100 m) and never
 * stored (PRIVACY rule 3).
 */
export function roundUserPosition(point: LatLng): LatLng {
	assertValid(point);
	return { lat: roundTo(point.lat, 3), lng: roundTo(point.lng, 3) };
}

export interface Landmark extends LatLng {
	name: string;
}

/** Landmarks further than this from the cell center are not used as "near X". */
export const MAX_LANDMARK_DISTANCE_M = 2_000;

/**
 * Nearest landmark (metro station) to a cell center, or null if none is within `maxDistanceM`.
 * Pass the cell center, never the exact point, so the label cannot narrow the location further.
 */
export function nearestLandmark<L extends Landmark>(
	cellCenter: LatLng,
	landmarks: readonly L[],
	maxDistanceM: number = MAX_LANDMARK_DISTANCE_M,
): L | null {
	let best: L | null = null;
	let bestDistance = Infinity;
	for (const landmark of landmarks) {
		const distance = distanceMeters(cellCenter, landmark);
		if (distance < bestDistance) {
			best = landmark;
			bestDistance = distance;
		}
	}
	return bestDistance <= maxDistanceM ? best : null;
}

/** `area_label` text for a metro landmark (SCREENS copy, Ukrainian). */
export function formatAreaLabel(landmarkName: string): string {
	return `Біля м. ${landmarkName}`;
}
